import type { PropertyDescriptor, SourceContext, SourceStatus, SourceTypeDefinition } from '../core/definitions';
import { bool, num, str } from './base';
import { mapMediaError } from './errors';
import { StreamRuntime, stopTracks, watchEnded } from './streamRuntime';

export type DisplayCaptureSettings = {
  captureAudio: boolean;
  cursor: 'always' | 'motion' | 'never';
  frameRate: number;
  surface: 'any' | 'monitor' | 'window' | 'browser';
  /** Largest captured height in px; 0 keeps the native size. Caps the cost of 5K and Retina surfaces. */
  maxHeight: number;
  contentHint: '' | 'detail' | 'motion' | 'text';
  /** Whether this studio's own tab may be picked (it would mirror itself). */
  selfBrowserSurface: 'exclude' | 'include';
};

const DEFAULTS: DisplayCaptureSettings = {
  captureAudio: false,
  cursor: 'always',
  frameRate: 30,
  surface: 'any',
  maxHeight: 0,
  contentHint: '',
  selfBrowserSurface: 'exclude',
};

function read(s: Partial<DisplayCaptureSettings>): DisplayCaptureSettings {
  const cursor = str(s.cursor, DEFAULTS.cursor);
  const surface = str(s.surface, DEFAULTS.surface);
  const hint = str(s.contentHint, DEFAULTS.contentHint);
  return {
    captureAudio: bool(s.captureAudio, DEFAULTS.captureAudio),
    cursor: cursor === 'motion' || cursor === 'never' ? cursor : 'always',
    frameRate: num(s.frameRate, DEFAULTS.frameRate, 1, 120),
    surface: surface === 'monitor' || surface === 'window' || surface === 'browser' ? surface : 'any',
    maxHeight: Math.round(num(s.maxHeight, DEFAULTS.maxHeight, 0, 8640)),
    contentHint: hint === 'detail' || hint === 'motion' || hint === 'text' ? hint : '',
    selfBrowserSurface: s.selfBrowserSurface === 'include' ? 'include' : 'exclude',
  };
}

// Screen Capture constraints and options that lib.dom does not type yet.
type DisplayVideoConstraints = MediaTrackConstraints & { cursor?: string; displaySurface?: string };
type DisplayAudioConstraints = MediaTrackConstraints & { suppressLocalAudioPlayback?: boolean };
type DisplayOptions = Omit<DisplayMediaStreamOptions, 'video' | 'audio'> & {
  video: DisplayVideoConstraints;
  audio: boolean | DisplayAudioConstraints;
  selfBrowserSurface?: 'include' | 'exclude';
  systemAudio?: 'include' | 'exclude';
  surfaceSwitching?: 'include' | 'exclude';
  controller?: unknown;
};

export function displayConstraints(c: DisplayCaptureSettings, controller?: unknown): DisplayMediaStreamOptions {
  const video: DisplayVideoConstraints = { frameRate: { ideal: c.frameRate }, cursor: c.cursor };
  if (c.surface !== 'any') video.displaySurface = c.surface;
  if (c.maxHeight > 0) video.height = { max: c.maxHeight };
  const options: DisplayOptions = {
    video,
    // Shared audio goes to the mixer, so the picked tab must not also play it locally, and the
    // browser's voice processing would only damage it.
    audio: c.captureAudio
      ? { suppressLocalAudioPlayback: true, echoCancellation: false, noiseSuppression: false, autoGainControl: false }
      : false,
    selfBrowserSurface: c.selfBrowserSurface,
    systemAudio: c.captureAudio ? 'include' : 'exclude',
    surfaceSwitching: 'include',
  };
  if (controller) options.controller = controller;
  return options as DisplayMediaStreamOptions;
}

/** Settings that only take effect when the surface is picked again (the browser dialog cannot be bypassed). */
const REPICK_KEYS = ['captureAudio', 'cursor', 'surface', 'selfBrowserSurface'] as const;

interface FocusController {
  setFocusBehavior(behavior: 'focus-capturing-application' | 'no-focus-change'): void;
}

function makeController(): FocusController | null {
  const ctor = (globalThis as { CaptureController?: new () => FocusController }).CaptureController;
  try {
    return ctor ? new ctor() : null;
  } catch {
    return null;
  }
}

class DisplayCaptureRuntime extends StreamRuntime<DisplayCaptureSettings> {
  private cfg: DisplayCaptureSettings;
  private stream: MediaStream | null = null;
  private unwatch: (() => void) | null = null;
  private repickNeeded = false;

  constructor(ctx: SourceContext, settings: Partial<DisplayCaptureSettings>) {
    super(ctx);
    this.cfg = read(settings);
  }

  update(settings: DisplayCaptureSettings): void {
    if (this.destroyed) return;
    const prev = this.cfg;
    this.cfg = read(settings);
    const track = this.stream?.getVideoTracks()[0];
    if (!track || track.readyState !== 'live') return;
    if (REPICK_KEYS.some((k) => prev[k] !== this.cfg[k])) {
      this.repickNeeded = true;
      this.emit();
    }
    if (this.cfg.contentHint !== prev.contentHint) track.contentHint = this.cfg.contentHint;
    if (this.cfg.frameRate !== prev.frameRate || this.cfg.maxHeight !== prev.maxHeight) {
      const constraints: MediaTrackConstraints = { frameRate: { ideal: this.cfg.frameRate } };
      if (this.cfg.maxHeight > 0) constraints.height = { max: this.cfg.maxHeight };
      track.applyConstraints(constraints).then(
        () => this.emit(),
        (err) => this.log('debug', 'applyConstraints rejected', err),
      );
    }
  }

  /**
   * `start` and `retry` both open the browser's picker, so both must run synchronously inside the click
   * that triggered them. `stop` ends the share and returns the source to a Start affordance.
   */
  invoke(action: string): Promise<void> | void {
    if (this.destroyed) return;
    if (action === 'start' || action === 'retry') return this.start();
    if (action === 'stop') this.stop();
  }

  getLiveInfo(): Record<string, string> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track || track.readyState !== 'live') return {};
    const s = (track.getSettings?.() ?? {}) as MediaTrackSettings & { displaySurface?: string };
    const info: Record<string, string> = {};
    const surfaces: Record<string, string> = { monitor: 'Entire screen', window: 'Window', browser: 'Browser tab' };
    if (s.displaySurface) info.surface = surfaces[s.displaySurface] ?? s.displaySurface;
    if (s.width && s.height) info.resolution = `${s.width}×${s.height}`;
    if (s.frameRate) info.frameRate = `${Math.round(s.frameRate * 10) / 10} fps`;
    info.audio = this.stream!.getAudioTracks().length > 0 ? 'Shared' : this.cfg.captureAudio ? 'The chosen surface offers no audio' : 'Off';
    if (this.repickNeeded) info.note = 'Audio, cursor and surface changes apply the next time you select a source.';
    return info;
  }

  private releaseStream(): void {
    this.stopWatching();
    this.unwatch?.();
    this.unwatch = null;
    this.clearOutput();
    stopTracks(this.stream);
    this.stream = null;
  }

  private stop(): void {
    this.beginEpoch(); // also drops a picker that is still open
    this.releaseStream();
    this.repickNeeded = false;
    this.setStatus('idle', undefined, 'stopped');
  }

  private async start(): Promise<void> {
    // getDisplayMedia consumes the user activation of the call stack, so nothing may be awaited before it.
    if (!navigator.mediaDevices?.getDisplayMedia) {
      this.beginEpoch();
      this.fail('unsupported', 'getDisplayMedia is unavailable in this browser');
      return;
    }
    const epoch = this.beginEpoch();
    const controller = makeController();
    const pending = navigator.mediaDevices.getDisplayMedia(displayConstraints(this.cfg, controller));
    // The previous capture is kept until the new pick succeeds, so cancelling the picker does not drop it.
    this.setStatus('starting');
    let stream: MediaStream;
    try {
      stream = await pending;
    } catch (err) {
      if (!this.isCurrent(epoch)) return;
      const mapped = mapMediaError(err, { displayCapture: true });
      if (mapped.code === 'aborted') {
        const kept = this.stream?.getVideoTracks().some((t) => t.readyState === 'live');
        if (kept) this.setStatus('live');
        else this.setStatus('idle', mapped);
      } else {
        this.fail(mapped.code, mapped.message);
      }
      return;
    }
    if (!this.isCurrent(epoch)) {
      stopTracks(stream);
      return;
    }
    try {
      // Without this the browser raises the captured window over the studio after every pick.
      controller?.setFocusBehavior('no-focus-change');
    } catch (err) {
      this.log('debug', 'setFocusBehavior refused (only windows and tabs support it)', err);
    }
    this.releaseStream();
    this.stream = stream;
    this.repickNeeded = false;
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack && this.cfg.contentHint) videoTrack.contentHint = this.cfg.contentHint;
    this.unwatch = watchEnded(stream.getVideoTracks(), () => {
      // Keyed on the stream, not the epoch: a cancelled re-pick bumps the epoch but keeps this capture running.
      if (!this.destroyed && this.stream === stream) this.markEnded();
    });
    try {
      if (!(await this.present(stream, epoch, { video: true, audio: stream.getAudioTracks().length > 0 }))) return;
    } catch (err) {
      if (!this.isCurrent(epoch)) return;
      this.fail('unknown', err instanceof Error ? err.message : String(err));
      return;
    }
    if (stream.getVideoTracks().every((t) => t.readyState === 'ended')) {
      this.markEnded();
      return;
    }
    this.setStatus('live');
    this.watchLiveness(stream.getVideoTracks(), {
      code: 'unknown',
      message: 'The screen capture stopped delivering frames. The shared window may be minimised or hidden.',
    });
  }

  protected release(): void {
    this.releaseStream();
  }
}

export const displayCaptureDefinition: SourceTypeDefinition<DisplayCaptureSettings> = {
  kind: 'display-capture',
  displayName: 'Screen / Window Capture',
  caps: { video: true, audio: true, retry: true },
  category: 'capture',
  duplicate: 'reference',
  defaults: () => ({ ...DEFAULTS }),
  properties(_settings: DisplayCaptureSettings, status?: SourceStatus): PropertyDescriptor[] {
    const sharing = status?.state === 'live';
    const props: PropertyDescriptor[] = [
      {
        key: 'start',
        label: sharing ? 'Change shared screen / window / tab…' : 'Select screen / window / tab…',
        type: 'button',
        action: 'start',
        variant: 'primary',
        description: 'The browser asks which surface to share; this needs a click.',
      },
    ];
    if (sharing || status?.state === 'starting') {
      props.push({ key: 'stop', label: 'Stop sharing', type: 'button', action: 'stop', variant: 'danger' });
    }
    props.push(
      {
        key: 'surface',
        label: 'Preferred surface',
        type: 'select',
        options: [
          { value: 'any', label: 'Any' },
          { value: 'monitor', label: 'Entire screen' },
          { value: 'window', label: 'Window' },
          { value: 'browser', label: 'Browser tab' },
        ],
      },
      { key: 'captureAudio', label: 'Capture audio', type: 'boolean', description: 'Tabs offer audio on Chromium; the whole screen only on Windows and ChromeOS.' },
      {
        key: 'cursor',
        label: 'Cursor',
        type: 'select',
        options: [
          { value: 'always', label: 'Always' },
          { value: 'motion', label: 'Only when moving' },
          { value: 'never', label: 'Hidden' },
        ],
      },
      { key: 'frameRate', label: 'Frame rate', type: 'number', min: 1, max: 120, step: 1, unit: 'fps', applyOn: 'commit' },
      {
        key: 'maxHeight',
        label: 'Largest height',
        type: 'select',
        description: 'Caps the captured size; a 5K display is otherwise processed at full size every frame.',
        options: [
          { value: 0, label: 'Native' },
          { value: 2160, label: '2160p' },
          { value: 1440, label: '1440p' },
          { value: 1080, label: '1080p' },
          { value: 720, label: '720p' },
        ],
      },
      {
        key: 'contentHint',
        label: 'Content type',
        type: 'select',
        options: [
          { value: '', label: 'Automatic' },
          { value: 'detail', label: 'Detail (slides, documents)' },
          { value: 'text', label: 'Text' },
          { value: 'motion', label: 'Motion (video, games)' },
        ],
      },
      {
        key: 'selfBrowserSurface',
        label: 'This studio tab',
        type: 'select',
        description: 'Sharing the studio tab itself creates an endless mirror.',
        options: [
          { value: 'exclude', label: 'Hide from the picker' },
          { value: 'include', label: 'Allow' },
        ],
      },
      { key: 'liveSurface', label: 'Sharing', type: 'info', liveKey: 'surface' },
      { key: 'liveResolution', label: 'Delivering', type: 'info', liveKey: 'resolution' },
      { key: 'liveFrameRate', label: 'Frame rate', type: 'info', liveKey: 'frameRate' },
      { key: 'liveAudio', label: 'Audio', type: 'info', liveKey: 'audio' },
      { key: 'liveNote', label: '', type: 'info', liveKey: 'note' },
    );
    return props;
  },
  create: (ctx, settings) => new DisplayCaptureRuntime(ctx, settings),
};
